import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";

interface ProtectedRouteProps {
  children: ReactNode;
  /** If true, requires onboarding to be complete; otherwise redirects to /onboarding. */
  requireOnboarded?: boolean;
}

export function ProtectedRoute({
  children,
  requireOnboarded = false,
}: ProtectedRouteProps) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div
        data-testid="auth-bootstrap"
        className="min-h-screen grid place-items-center bg-background"
      >
        <div className="flex items-center gap-3 text-muted-foreground text-sm">
          <span className="w-2 h-2 rounded-full bg-oxblood animate-pulse" />
          Loading your workspace…
        </div>
      </div>
    );
  }

  if (status === "unauthenticated" || !user) {
    const redirectTo = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={redirectTo} replace />;
  }

  if (requireOnboarded && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();
  if (status === "loading") return null;
  if (status === "authenticated" && user) {
    const dest = user.onboarding_completed ? "/app" : "/onboarding";
    return <Navigate to={dest} replace />;
  }
  return <>{children}</>;
}
